import { remoteRequest } from './httpClient'

const ENDPOINT = '/admin/ai'
const ALLOWED_RESOURCES = new Set(['health', 'assistant', 'importer-assistant', 'custom-scripts', 'custom-script-users', 'users', 'prompts', 'settings', 'knowledge', 'review-queue', 'source-files', 'audit-log', 'costs'])
const BLOCKED_RESOURCES = new Set([
  'scripts',
  'script-files',
  'script_file',
  'script_files',
  'ai_script_files',
  'save-bundle',
  'generation',
  'generate',
])
const BLOCKED_ACTIONS = new Set(['save-bundle', 'generation', 'generate'])

function clean(value) {
  return value === undefined || value === null ? '' : String(value).trim()
}

function compactQuery(query) {
  return Object.entries(query || {}).reduce((acc, [key, value]) => {
    const next = clean(value)
    if (next) acc[key] = next
    return acc
  }, {})
}

function assertAllowedResource(resource) {
  const key = clean(resource)
  if (!key) throw new Error('Resource AI requerido')
  if (BLOCKED_RESOURCES.has(key) || !ALLOWED_RESOURCES.has(key)) {
    throw new Error('Recurso fuera de alcance para esta fase')
  }
  return key
}

function assertAllowedAction(resource, action) {
  const normalizedResource = assertAllowedResource(resource)
  const normalizedAction = clean(action)
  const isAssistantGenerate = normalizedResource === 'assistant' && normalizedAction === 'generate'
  const isAssistantStatus = normalizedResource === 'assistant' && normalizedAction === 'status'
  const isImporterAssistantGenerate = normalizedResource === 'importer-assistant' && normalizedAction === 'generate'
  const isImporterAssistantStatus = normalizedResource === 'importer-assistant' && normalizedAction === 'status'
  const isSourceFilesUpload = normalizedResource === 'source-files' && normalizedAction === 'upload'
  const isSourceFilesDownload = normalizedResource === 'source-files' && normalizedAction === 'download'
  const isSourceFilesList = normalizedResource === 'source-files' && normalizedAction === 'list'
  const isSourceFilesDelete = normalizedResource === 'source-files' && normalizedAction === 'delete'
  const isCustomScriptsSaveDraft = normalizedResource === 'custom-scripts' && normalizedAction === 'save-draft'
  const isCustomScriptsValidatePublish = normalizedResource === 'custom-scripts' && normalizedAction === 'validate-publish'
  const isCustomScriptsPublishTest = normalizedResource === 'custom-scripts' && normalizedAction === 'publish-test'
  const isCustomScriptsPublishReleaseTest = normalizedResource === 'custom-scripts' && normalizedAction === 'publish-release-test'
  const isCustomScriptsPublishRelease = normalizedResource === 'custom-scripts' && normalizedAction === 'publish-release'
  const isCustomScriptsPublishedFile = normalizedResource === 'custom-scripts' && normalizedAction === 'published-file'
  const isCustomScriptsGlobalAvailability = normalizedResource === 'custom-scripts' &&
    ['global-availability', 'set-global-availability'].includes(normalizedAction)
  const isUsersByParentPcc = normalizedResource === 'users' && normalizedAction === 'by-parentpcc'
  const isCustomScriptUsersByParentPcc = normalizedResource === 'custom-script-users' && normalizedAction === 'list-by-parentpcc'
  if (normalizedResource === 'assistant' && !isAssistantGenerate && !isAssistantStatus) {
    throw new Error('Recurso fuera de alcance para esta fase')
  }
  if (normalizedResource === 'importer-assistant' && !isImporterAssistantGenerate && !isImporterAssistantStatus) {
    throw new Error('Recurso fuera de alcance para esta fase')
  }
  if (normalizedResource === 'source-files' && !isSourceFilesUpload && !isSourceFilesDownload && !isSourceFilesList && !isSourceFilesDelete) {
    throw new Error('Recurso fuera de alcance para esta fase')
  }
  if (
    normalizedResource === 'custom-scripts' &&
    !isCustomScriptsSaveDraft &&
    !isCustomScriptsValidatePublish &&
    !isCustomScriptsPublishTest &&
    !isCustomScriptsPublishReleaseTest &&
    !isCustomScriptsPublishRelease &&
    !isCustomScriptsPublishedFile &&
    !isCustomScriptsGlobalAvailability
  ) {
    throw new Error('Recurso fuera de alcance para esta fase')
  }
  if (normalizedResource === 'users' && !isUsersByParentPcc) {
    throw new Error('Recurso fuera de alcance para esta fase')
  }
  if (normalizedResource === 'custom-script-users' && !isCustomScriptUsersByParentPcc) {
    throw new Error('Recurso fuera de alcance para esta fase')
  }
  if (
    !isAssistantGenerate &&
    !isAssistantStatus &&
    !isImporterAssistantGenerate &&
    !isImporterAssistantStatus &&
    !isSourceFilesUpload &&
    !isSourceFilesDownload &&
    !isSourceFilesList &&
    !isSourceFilesDelete &&
    !isCustomScriptsSaveDraft &&
    !isCustomScriptsValidatePublish &&
    !isCustomScriptsPublishTest &&
    !isCustomScriptsPublishReleaseTest &&
    !isCustomScriptsPublishRelease &&
    !isCustomScriptsPublishedFile &&
    !isUsersByParentPcc &&
    !isCustomScriptUsersByParentPcc &&
    BLOCKED_ACTIONS.has(normalizedAction)
  ) {
    throw new Error('Recurso fuera de alcance para esta fase')
  }
  return normalizedResource
}

function idQuery(resource, body = {}) {
  assertAllowedAction(resource, body.action)
  const query = { resource, action: body.action }
  if (body.id) query.id = body.id
  if (body.key) query.key = body.key
  if (body.uuid) query.uuid = body.uuid
  if (body.agent) query.agent = body.agent
  if (body.category) query.category = body.category
  if (body.status) query.status = body.status
  if (body.estado) query.estado = body.estado
  if (body.origin) query.origin = body.origin
  if (body.scriptUuid) query.scriptUuid = body.scriptUuid
  if (body.entity) query.entity = body.entity
  if (body.entityId) query.entityId = body.entityId
  if (body.parentPcc) query.parentPcc = body.parentPcc
  if (body.pcc) query.pcc = body.pcc
  if (body.page) query.page = body.page
  if (body.pageSize) query.pageSize = body.pageSize
  if (body.updatedUser) query.updatedUser = body.updatedUser
  return compactQuery(query)
}

export function aiAdminRequest(method, query, body) {
  assertAllowedAction(query?.resource, query?.action)
  return remoteRequest(method, ENDPOINT, {
    query: compactQuery(query),
    body,
    timeout: 30000,
  })
}

export async function listAIAdmin(params = {}) {
  const resource = assertAllowedResource(params.resource)
  let action = 'list'
  const query = { resource }

  if (resource === 'costs') {
    const costsAction = clean(params.action) || 'list'
    assertAllowedAction(resource, costsAction)
    const filter = params.data && typeof params.data === 'object'
      ? params.data
      : Object.fromEntries(
          Object.entries({
            scriptUuid: params.scriptUuid,
            scriptName: params.scriptName,
            runId: params.runId,
            days: params.days,
            limit: params.limit,
          }).filter(([, value]) => value !== undefined && value !== null && value !== ''),
        )
    return aiAdminRequest('POST', { resource, action: costsAction }, filter)
  }

  if (resource === 'prompts' && params.agent) {
    action = 'list-by-agent'
    query.agent = params.agent
  }
  if (resource === 'knowledge' && params.active) action = 'list-active'
  if (resource === 'knowledge' && params.category) {
    action = 'list-by-category'
    query.category = params.category
  }
  if (resource === 'review-queue' && params.estado) {
    action = 'list-by-estado'
    query.estado = params.estado
  }
  if (resource === 'review-queue' && params.scriptUuid) {
    action = 'list-by-script'
    query.scriptUuid = params.scriptUuid
  }
  if (resource === 'audit-log' && params.entity && params.entityId) {
    action = 'list-by-entity-and-entity-id'
    query.entity = params.entity
    query.entityId = params.entityId
  } else if (resource === 'audit-log' && params.entity) {
    action = 'list-by-entity'
    query.entity = params.entity
  } else if (resource === 'audit-log' && params.entityId) {
    action = 'list-by-entity-id'
    query.entityId = params.entityId
  }

  query.action = action
  return aiAdminRequest('GET', query)
}

export function getAIAdmin(params = {}) {
  return aiAdminRequest('GET', idQuery(params.resource, { ...params, action: params.action || 'get' }))
}

export function createAIAdmin(body = {}) {
  const { resource, action = 'save', data = {} } = body
  assertAllowedAction(resource, action)
  return aiAdminRequest('POST', { resource, action }, data)
}

export function generateAIAssistant(body = {}) {
  return aiAdminRequest('POST', { resource: 'assistant', action: 'generate' }, body)
}

export function statusAIAssistant(body = {}) {
  const jobId = body?.jobId || body?.id || ''
  const query = { resource: 'assistant', action: 'status', id: jobId }
  assertAllowedAction(query.resource, query.action)
  return remoteRequest('POST', ENDPOINT, {
    query,
    body,
    timeout: 15000,
  })
}

export function generateAIImporterAssistant(body = {}) {
  const query = { resource: 'importer-assistant', action: 'generate' }
  assertAllowedAction(query.resource, query.action)
  // Con el pattern async el POST debe volver rapido con { jobId, status: "pending" }.
  // Timeout corto para detectar backend que quedo colgado antes de encolar.
  return remoteRequest('POST', ENDPOINT, {
    query,
    body,
    timeout: 30000,
  })
}

export function statusAIImporterAssistant(body = {}) {
  const jobId = body?.jobId || body?.id || ''
  const query = { resource: 'importer-assistant', action: 'status', id: jobId }
  assertAllowedAction(query.resource, query.action)
  return remoteRequest('POST', ENDPOINT, {
    query,
    body,
    timeout: 15000,
  })
}

export function validateAICustomScriptPublish(body = {}) {
  return aiAdminRequest('POST', { resource: 'custom-scripts', action: 'validate-publish' }, body)
}

export function saveDraftAICustomScript(body = {}) {
  return aiAdminRequest('POST', { resource: 'custom-scripts', action: 'save-draft' }, body)
}

export function publishTestAICustomScript(body = {}) {
  return aiAdminRequest('POST', { resource: 'custom-scripts', action: 'publish-test' }, body)
}

export function publishReleaseTestAICustomScript(body = {}) {
  return aiAdminRequest('POST', { resource: 'custom-scripts', action: 'publish-release-test' }, body)
}

export function publishReleaseAICustomScript(body = {}) {
  return aiAdminRequest('POST', { resource: 'custom-scripts', action: 'publish-release' }, body)
}

export function fetchPublishedAICustomScriptFile(body = {}) {
  return aiAdminRequest('POST', { resource: 'custom-scripts', action: 'published-file' }, body)
}

export function listAIUsersByParentPcc(params = {}) {
  return aiAdminRequest('GET', {
    resource: 'users',
    action: 'by-parentpcc',
    parentPcc: params.parentPcc,
    page: params.page || '1',
    pageSize: params.pageSize || '100',
  })
}

export function listAICustomScriptUsersByParentPcc(params = {}) {
  return aiAdminRequest('GET', {
    resource: 'custom-script-users',
    action: 'list-by-parentpcc',
    uuid: params.uuid,
  })
}

export function upsertAISetting(body = {}) {
  return aiAdminRequest('POST', { resource: 'settings', action: 'upsert' }, body.data || {})
}

export function updateAIAdmin(body = {}) {
  const { resource, data = {} } = body
  assertAllowedResource(resource)
  return aiAdminRequest('PUT', idQuery(resource, { ...body, action: 'update' }), data)
}

export function deleteAIAdmin(body = {}) {
  return aiAdminRequest('DELETE', idQuery(body.resource, { ...body, action: 'delete' }))
}

export function updateAIAdminStatus(body = {}) {
  const { resource, data = {} } = body
  assertAllowedResource(resource)
  const action = body.action || (resource === 'review-queue' ? 'estado' : 'status')
  return aiAdminRequest('PATCH', idQuery(resource, { ...body, action }), data)
}

export function activateAIPrompt(body = {}) {
  return aiAdminRequest('PATCH', idQuery('prompts', { ...body, resource: 'prompts', action: 'activate' }))
}

export function deactivateAIAdmin(body = {}) {
  return aiAdminRequest('PATCH', idQuery(body.resource, { ...body, action: 'deactivate' }))
}

export function registerAIAuditAction(body = {}) {
  return aiAdminRequest('POST', { resource: 'audit-log', action: 'save' }, body)
}
